import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TablePagination,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useAuth } from "../Context/AuthProvider";
import { fetchCounterparties, deleteCounterparty } from "../http/api";
import CounterPartyTable from "../Component/CounterParty/CounterPartyTable.jsx";
import AddCounterpartyDialog from "../Component/TradeEntry/AddCounterpartyDialog.jsx";
import { tokens, withAlpha } from "../Common/designTokens"; // added
import { usePageSubscription } from "../Utils/WebSocketProvider.jsx";

const CounterParties = () => {
  const { token } = useAuth();
  usePageSubscription("counterParties");
  const [data, setData] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);

  // Pagination and sorting
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: "counterPartyName", direction: "asc" });

  // Add/Edit dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("add"); // "add" | "edit"
  const [editItem, setEditItem] = useState(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const resp = await fetchCounterparties(
        token,
        page,
        rowsPerPage,
        sortConfig.key || "counterPartyName",
        sortConfig.direction || "asc"
      );
      // payload: { content, totalElements, ... }
      setData(resp?.content || []);
      setTotalElements(resp?.totalElements || 0);
    } catch (e) {
      setData([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page, rowsPerPage, sortConfig.key, sortConfig.direction]);

  const handleSort = (columnKey, newDirection) => {
    setPage(0);
    setSortConfig({ key: columnKey, direction: newDirection });
  };

  const handleAddOpen = () => {
    setDialogMode("add");
    setEditItem(null);
    setDialogOpen(true);
  };

  const handleEditOpen = (item) => {
    setDialogMode("edit");
    setEditItem(item);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditItem(null);
  };

  const handleSubmitSuccess = () => {
    // Close dialog and let websocket update the table; avoid API refetch
    handleDialogClose();
    setPage(0);
    // ...no loadData() here...
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCounterparty(token, deleteTarget.counterPartyMstId);
      setDeleteTarget(null);
      // Let websocket event update the table; avoid API refetch and local count math
      // ...no loadData() or manual count adjustment...
    } catch (e) {
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const handleCounterPartyUpdate = (event) => {
      // Backend now sends a plain DTO
      const cp = event.detail || {};
      const id = cp.counterPartyMstId;
      if (!id) return;

      // Derive action client-side
      const isDelete = Number(cp.isActive) === 0;

      // Debug log similar to other WS handlers
      console.log("debugging", "/topic/counter-party (screen)", { derivedAction: isDelete ? "DELETE" : "UPSERT", data: cp });

      setData((prev) => {
        const idx = prev.findIndex((x) => x.counterPartyMstId === id);

        if (isDelete) {
          if (idx === -1) return prev;
          const updated = prev.filter((x) => x.counterPartyMstId !== id);
          return updated;
        }

        if (idx !== -1) {
          // Update existing row
          const updated = [...prev];
          updated[idx] = { ...updated[idx], ...cp };
          return updated;
        }

        // Create: prepend and cap to current page size
        const updated = [cp, ...prev];
        return updated.slice(0, rowsPerPage);
      });

      // Update total count conservatively
      setTotalElements((prevTotal) => {
        const existsNow = data.some((x) => x.counterPartyMstId === id);
        if (isDelete) {
          // If it existed, decrement
          return Math.max(0, prevTotal - (existsNow ? 1 : 0));
        } else {
          // If it did not exist before, increment
          return prevTotal + (existsNow ? 0 : 1);
        }
      });
    };

    window.addEventListener("counterPartyUpdate", handleCounterPartyUpdate);
    return () => window.removeEventListener("counterPartyUpdate", handleCounterPartyUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, data]);

  return (
    <Box
      sx={{
        px: "16px",
        py: "12px",
        height: `calc(100vh - 120px)`, 
        display: "flex",
        flexDirection: "column",
        gap: 1,
        // pb: "72px", // ensure content stays above fixed pagination
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Counterparties
        </Typography>
        <Button
          color="inherit"
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            display: "flex",
            height: 42,
            textTransform: "none",
            boxShadow: "none",
            borderRadius: "8px",
            backgroundColor: tokens.colors.defaultGridRowColor,
            color: tokens.colors.fontColour,
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            width: { xs: "100%", sm: "auto" },
            "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
            fontWeight: 700,
          }}
          onClick={handleAddOpen}
        >
          Add Counterparty
        </Button>
      </Box>

      {/* Content: make table take all available height */}
      <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
        <CounterPartyTable
          data={data}
          loading={loading} // show loader inside table
          onEdit={handleEditOpen}
          onDelete={(item) => setDeleteTarget(item)}
          onSort={handleSort}
          sortConfig={sortConfig}
        />
      </Box>

      {/* Pagination fixed at bottom similar to other screens */}
      <Box sx={{ position: "fixed", bottom: 0, left: 0, width: "100%", zIndex: 1000, display: "flex", justifyContent: "end" }}>
        <TablePagination
          component="footer"
          sx={{
            ".MuiSelect-select": { p: "4px 24px 5px 8px !important" },
            color: tokens.colors.fontColour,
            "& .MuiSelect-select": { color: tokens.colors.fontColour },
            "& .MuiIconButton-root": { color: tokens.colors.fontColour85 },
            "& .MuiTablePagination-displayedRows": { color: tokens.colors.fontColour85 },
          }}
          showFirstButton
          showLastButton
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[20, 50, 100]}
          page={page}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onPageChange={handleChangePage}
          count={totalElements}
          labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString()}–${to.toLocaleString()} of ${count.toLocaleString()}`}
        />
      </Box>

      {/* Add/Edit Dialog */}
      {dialogOpen && (
        <AddCounterpartyDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          mode={dialogMode}
          initialData={editItem}
          onSubmit={handleSubmitSuccess}
        />
      )}

      {/* Delete confirm */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: tokens.colors.bodyBackgroundColor,
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            boxShadow: 24,
            // pt: 2,
            pb: 2,
            borderRadius: "8px",
            m: 0,
           },
         }}
       >
        <DialogTitle sx={{ color: tokens.palette.danger, fontWeight: 700, textAlign: "left" }}>
          Delete Counterparty
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: tokens.colors.fontColour, mb: 1, mt: 1 }}>
            Are you sure you want to delete “{deleteTarget?.counterPartyName}”?
          </Typography>
        </DialogContent>
        <DialogActions sx={{  pb: 1,pr:3 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            variant="outlined"
            sx={{
               textTransform: "none",
               fontWeight: "700",
               color: tokens.colors.fontColour,
               borderColor: tokens.colors.bodyBorderColor50,
               width: "120px",
               borderRadius: "8px",
               "&:hover": {
                 borderColor: tokens.colors.bodyBorderColor50,
                 backgroundColor: tokens.colors.defaultGridHoverColor,
               },
             }}
             disabled={deleting}
           >
             Cancel
           </Button>
           <Button
             onClick={handleDelete}
             color="error"
             variant="contained"
             disabled={deleting}
             startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : null}
             sx={{
               textTransform: "none",
               fontWeight: "700",
               width: "140px",
               borderRadius: "8px",
             }}
           >
             {deleting ? "Processing..." : "Delete"}
           </Button>
         </DialogActions>
       </Dialog>
     </Box>
   );
 };

 export default CounterParties;
